import { getSupabaseBrowserClient, isSupabaseConfigured } from "@/auth/supabase";

/**
 * Live "something changed" signals for staff, from the database (i67).
 *
 * AFTER-insert triggers on support_ticket, support_conversation and report
 * broadcast a content-free signal on PRIVATE Realtime topics. Realtime lets a
 * staff session join only if RLS on realtime.messages says so:
 *
 *   hq-support       — support viewers        (ticket, conversation, ticket_status)
 *   hq-trust-safety  — trust_safety viewers   (report)
 *
 * The payload carries ids and a priority, never content; the listener re-reads
 * through the API as usual, so authorization is never decided here. A staff
 * member without the permission simply never receives anything, and polling
 * still runs underneath for the day the socket is down.
 */

export type StaffSignal =
  | { topic: "hq-support"; event: "ticket" | "conversation" | "ticket_status"; payload: Record<string, unknown> }
  | { topic: "hq-trust-safety"; event: "report"; payload: Record<string, unknown> };

export function subscribeStaffSignals(onSignal: (s: StaffSignal) => void): () => void {
  if (!isSupabaseConfigured()) return () => undefined;
  const supabase = getSupabaseBrowserClient();

  const support = supabase
    .channel("hq-support", { config: { private: true } })
    .on("broadcast", { event: "ticket" }, (m) => onSignal({ topic: "hq-support", event: "ticket", payload: m.payload ?? {} }))
    .on("broadcast", { event: "conversation" }, (m) => onSignal({ topic: "hq-support", event: "conversation", payload: m.payload ?? {} }))
    .on("broadcast", { event: "ticket_status" }, (m) => onSignal({ topic: "hq-support", event: "ticket_status", payload: m.payload ?? {} }))
    .subscribe((status, err) => {
      if (status !== "SUBSCRIBED" && status !== "CLOSED") console.info("[staff-signals] hq-support", status, err?.message ?? "");
    });

  const safety = supabase
    .channel("hq-trust-safety", { config: { private: true } })
    .on("broadcast", { event: "report" }, (m) => onSignal({ topic: "hq-trust-safety", event: "report", payload: m.payload ?? {} }))
    .subscribe((status, err) => {
      if (status !== "SUBSCRIBED" && status !== "CLOSED") console.info("[staff-signals] hq-trust-safety", status, err?.message ?? "");
    });

  return () => {
    void supabase.removeChannel(support);
    void supabase.removeChannel(safety);
  };
}

/**
 * A short two-tone chime, synthesised — no audio file to ship or cache. A
 * browser only lets a page make sound after the user has interacted with it,
 * so the first alert of a fresh, untouched tab may be silent; the badge, the
 * tab title and the pop-up still show.
 */
let audio: AudioContext | null = null;
export function playAlertChime(urgent: boolean) {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    audio ??= new Ctx();
    if (audio.state === "suspended") void audio.resume();
    const now = audio.currentTime;
    const notes = urgent ? [880, 660, 880] : [660, 880];
    notes.forEach((freq, i) => {
      const osc = audio!.createOscillator();
      const gain = audio!.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t = now + i * 0.16;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
      osc.connect(gain).connect(audio!.destination);
      osc.start(t);
      osc.stop(t + 0.15);
    });
  } catch (e) {
    console.info("[staff-alerts] chime unavailable", e);
  }
}

/** A desktop notification when the HQ tab is in the background, if allowed. */
export function desktopNotify(title: string, body: string, onClick: () => void) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  if (document.visibilityState === "visible") return;
  try {
    const n = new Notification(title, { body, tag: title });
    n.onclick = () => {
      window.focus();
      onClick();
      n.close();
    };
  } catch (e) {
    console.info("[staff-alerts] desktop notification failed", e);
  }
}

/** One ticket's live signals (new message, status change), for the desk view. */
export function subscribeTicketSignals(ticketId: string, onSignal: () => void): () => void {
  if (!isSupabaseConfigured()) return () => undefined;
  const supabase = getSupabaseBrowserClient();
  const channel = supabase
    .channel(`support-ticket:${ticketId}`, { config: { private: true } })
    .on("broadcast", { event: "message" }, () => onSignal())
    .on("broadcast", { event: "status" }, () => onSignal())
    .subscribe();
  return () => { void supabase.removeChannel(channel); };
}
